import React from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle, ArrowRight } from "lucide-react";

/**
 * NetworkConfirmDialog — the "unrecoverable loss" guardrail.
 *
 * Surfaces an explicit, friction-by-design confirmation BEFORE we send the
 * customer off to SEPA/Card checkout. Once funds are sent on a wrong network
 * (e.g. USDC-ERC20 sent to a Solana address), they are GONE. This dialog is
 * the last moment to catch a misclick.
 *
 * Behaviour:
 *  - Customer must tick "I confirm" to enable the proceed button.
 *  - Address is shown in monospace, full-width, on a contrast background so
 *    typos are visible.
 *  - Network and asset are bolded — the two fields that most commonly cause
 *    misroutes.
 */
export function NetworkConfirmDialog({
  open,
  onConfirm,
  onCancel,
  asset,
  network,
  receiveAmount,
  walletAddress,
  fiatAmount,
  fiatCurrency = "EUR",
  paymentMethod = "sepa",
}) {
  const [ack, setAck] = React.useState(false);
  React.useEffect(() => { if (!open) setAck(false); }, [open]);

  const goLabel = paymentMethod === "card"
    ? "Continue to card payment"
    : "Continue to SEPA payment";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        className="max-w-lg rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{ maxHeight: "min(92vh, calc(100dvh - 2rem))" }}
        data-testid="network-confirm-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <DialogTitle className="text-lg font-display font-extrabold">
              Confirm your delivery details
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Crypto sent to the wrong network or address is{" "}
            <strong className="text-foreground">unrecoverable</strong>.
            Please double-check before you continue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          <Field label="You'll receive">
            <span className="font-display text-base font-extrabold tabular-nums" data-testid="confirm-receive-amount">
              {receiveAmount} <span className="text-primary">{asset}</span>
            </span>
          </Field>
          <Field label="On network">
            <span className="font-display text-base font-extrabold text-foreground" data-testid="confirm-network">
              {network}
            </span>
          </Field>
          <Field label="To wallet">
            <code className="text-xs font-mono break-all bg-muted/40 px-2 py-1.5 rounded-md block w-full"
                  data-testid="confirm-wallet-address">
              {walletAddress}
            </code>
          </Field>
          <Field label="You'll pay">
            <span className="font-display text-base font-extrabold tabular-nums">
              {Number(fiatAmount).toFixed(2)} {fiatCurrency}
            </span>
          </Field>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-relaxed">
          <strong>{network}</strong> is not interchangeable with other networks
          for the same asset. If your receiving wallet doesn't support{" "}
          <strong>{network}</strong>, your funds will be lost.
        </div>

        <label className="flex items-start gap-2 mt-3 cursor-pointer select-none"
               data-testid="confirm-ack-label">
          <Checkbox
            checked={ack}
            onCheckedChange={(v) => setAck(!!v)}
            data-testid="confirm-ack-checkbox"
            className="mt-0.5"
          />
          <span className="text-xs leading-relaxed text-foreground">
            I confirm the wallet address and network are correct, and I understand
            that crypto sent to the wrong network or address cannot be recovered.
          </span>
        </label>

        <DialogFooter className="gap-2 mt-3">
          <Button onClick={onCancel} variant="outline" className="rounded-xl"
                  data-testid="confirm-cancel-btn">
            Back
          </Button>
          <Button onClick={onConfirm} disabled={!ack}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="confirm-proceed-btn">
            {goLabel} <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground pt-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

export default NetworkConfirmDialog;
